import React, { useState } from 'react';
import { UserIcon, PlusIcon, EditIcon, AlertTriangleIcon, CalendarIcon, PhoneIcon, MailIcon, CreditCardIcon, ClipboardIcon } from 'lucide-react';
interface Driver {
  id: string;
  name: string;
  email: string;
  phone: string;
  cdlNumber: string;
  cdlClass: string;
  cdlExpiration: string;
  medicalCardExpiration: string;
  hireDate: string;
  status: 'active' | 'inactive' | 'on-leave';
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  };
}
interface DriverManagementProps {
  drivers: Driver[];
}
export const DriverManagement = ({
  drivers
}: DriverManagementProps) => {
  const [showAddDriverModal, setShowAddDriverModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'on-leave':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const isExpired = (dateString: string) => {
    return new Date(dateString) < new Date();
  };
  const isExpiringSoon = (dateString: string) => {
    const date = new Date(dateString);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return date <= thirtyDaysFromNow && date > new Date();
  };
  const getExpirationStatus = (dateString: string) => {
    if (isExpired(dateString)) {
      return {
        color: 'text-red-600',
        label: 'EXPIRED',
        icon: <AlertTriangleIcon size={12} data-id="element-3612" />
      };
    }
    if (isExpiringSoon(dateString)) {
      return {
        color: 'text-yellow-600',
        label: 'EXPIRES SOON',
        icon: <CalendarIcon size={12} data-id="element-3613" />
      };
    }
    return {
      color: 'text-green-600',
      label: 'VALID',
      icon: null
    };
  };
  return <div className="bg-white rounded-md shadow p-6" data-id="element-3614">
      <div className="flex justify-between items-center mb-6" data-id="element-3615">
        <div data-id="element-3616">
          <h2 className="text-xl font-bold" data-id="element-3617">Driver Management</h2>
          <p className="text-gray-600 mt-1" data-id="element-3618">
            Manage drivers, CDL information, and medical card renewals
          </p>
        </div>
        <button onClick={() => setShowAddDriverModal(true)} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center gap-2" data-id="element-3619">
          <PlusIcon size={18} data-id="element-3620" />
          Add New Driver
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3621">
        {drivers.map(driver => {
        const cdlStatus = getExpirationStatus(driver.cdlExpiration);
        const medicalStatus = getExpirationStatus(driver.medicalCardExpiration);
        const hasExpiredDocs = isExpired(driver.cdlExpiration) || isExpired(driver.medicalCardExpiration);
        return <div key={driver.id} className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${hasExpiredDocs ? 'border-red-200 bg-red-50' : ''}`} data-id="element-3622">
              <div className="flex items-center justify-between mb-4" data-id="element-3623">
                <div className="flex items-center gap-3" data-id="element-3624">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-3625">
                    <UserIcon size={20} className="text-blue-600" data-id="element-3626" />
                  </div>
                  <div data-id="element-3627">
                    <h3 className="font-semibold text-lg" data-id="element-3628">{driver.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(driver.status)}`} data-id="element-3629">
                      {driver.status.replace('-', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
                <button onClick={() => setSelectedDriver(driver)} className="text-gray-400 hover:text-gray-600" data-id="element-3630">
                  <EditIcon size={18} data-id="element-3631" />
                </button>
              </div>
              <div className="space-y-3 mb-4" data-id="element-3632">
                <div className="flex items-center gap-2 text-sm" data-id="element-3633">
                  <MailIcon size={14} className="text-gray-400" data-id="element-3634" />
                  <span data-id="element-3635">{driver.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm" data-id="element-3636">
                  <PhoneIcon size={14} className="text-gray-400" data-id="element-3637" />
                  <span data-id="element-3638">{driver.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-sm" data-id="element-3639">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-3640" />
                  <span data-id="element-3641">
                    Hired: {new Date(driver.hireDate).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="border-t pt-4 space-y-3" data-id="element-3642">
                <div className="flex items-center justify-between" data-id="element-3643">
                  <div className="flex items-center gap-2" data-id="element-3644">
                    <CreditCardIcon size={16} className="text-gray-400" data-id="element-3645" />
                    <span className="text-sm font-medium" data-id="element-3646">
                      CDL {driver.cdlClass}
                    </span>
                  </div>
                  <div className={`flex items-center gap-1 text-xs font-medium ${cdlStatus.color}`} data-id="element-3647">
                    {cdlStatus.icon}
                    {cdlStatus.label}
                  </div>
                </div>
                <div className="text-xs text-gray-500 ml-6" data-id="element-3648">
                  #{driver.cdlNumber} • Expires:{' '}
                  {new Date(driver.cdlExpiration).toLocaleDateString()}
                </div>
                <div className="flex items-center justify-between" data-id="element-3649">
                  <div className="flex items-center gap-2" data-id="element-3650">
                    <ClipboardIcon size={16} className="text-gray-400" data-id="element-3651" />
                    <span className="text-sm font-medium" data-id="element-3652">Medical Card</span>
                  </div>
                  <div className={`flex items-center gap-1 text-xs font-medium ${medicalStatus.color}`} data-id="element-3653">
                    {medicalStatus.icon}
                    {medicalStatus.label}
                  </div>
                </div>
                <div className="text-xs text-gray-500 ml-6" data-id="element-3654">
                  Expires:{' '}
                  {new Date(driver.medicalCardExpiration).toLocaleDateString()}
                </div>
              </div>
              <div className="border-t pt-3 mt-4" data-id="element-3655">
                <div className="text-xs text-gray-500" data-id="element-3656">
                  <span className="font-medium" data-id="element-3657">Emergency Contact:</span>{' '}
                  {driver.emergencyContact.name} (
                  {driver.emergencyContact.relationship}) -{' '}
                  {driver.emergencyContact.phone}
                </div>
              </div>
              {hasExpiredDocs && <div className="mt-3 bg-red-100 border border-red-200 rounded-md p-2" data-id="element-3658">
                  <p className="text-xs text-red-600 flex items-center gap-1 font-medium" data-id="element-3659">
                    <AlertTriangleIcon size={12} data-id="element-3660" />
                    Driver cannot operate - expired documents
                  </p>
                </div>}
            </div>;
      })}
      </div>
      {drivers.length === 0 && <div className="text-center py-12" data-id="element-3661">
          <UserIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-3662" />
          <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-3663">
            No drivers in your team
          </h3>
          <p className="text-gray-600 mb-4" data-id="element-3664">
            Add your first driver to start managing your team
          </p>
          <button onClick={() => setShowAddDriverModal(true)} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md" data-id="element-3665">
            Add Your First Driver
          </button>
        </div>}
      {/* Add Driver Modal would go here */}
      {showAddDriverModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-3666">
          <div className="bg-white rounded-lg p-6 w-full max-w-md" data-id="element-3667">
            <h3 className="text-lg font-semibold mb-4" data-id="element-3668">Add New Driver</h3>
            <p className="text-gray-600 mb-4" data-id="element-3669">
              Add driver form would be implemented here
            </p>
            <div className="flex justify-end gap-3" data-id="element-3670">
              <button onClick={() => setShowAddDriverModal(false)} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3671">
                Cancel
              </button>
              <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700" data-id="element-3672">
                Add Driver
              </button>
            </div>
          </div>
        </div>}
    </div>;
};